import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Plus, 
  Users, 
  Trash2,
  Mail,
  Shield,
  UserCog,
  User as UserIcon,
  Building2
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const UsersPage = ({ user }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    role: "guest"
  });

  // Only superuser can access this page
  useEffect(() => {
    if (user?.role !== "superuser") {
      toast.error("Anda tidak memiliki akses ke halaman ini");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.role === "superuser") {
      fetchUsers();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Gagal memuat data pengguna");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.email.trim() || !formData.name.trim()) {
        toast.error("Email dan nama wajib diisi");
        return;
      }
      
      await axios.post(`${API}/users`, formData);
      toast.success("Pengguna berhasil ditambahkan");
      setIsCreateOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error("Error creating user:", error);
      toast.error(error.response?.data?.detail || "Gagal menambahkan pengguna");
    }
  };

  const handleEdit = async () => {
    try {
      await axios.put(`${API}/users/${selectedUser.user_id}`, {
        name: formData.name,
        role: formData.role
      });
      toast.success("Pengguna berhasil diupdate");
      setIsEditOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error(error.response?.data?.detail || "Gagal mengupdate pengguna");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/users/${selectedUser.user_id}`);
      toast.success("Pengguna berhasil dihapus");
      setIsDeleteOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.response?.data?.detail || "Gagal menghapus pengguna");
    }
  };

  const openEditDialog = (userItem) => {
    setSelectedUser(userItem);
    setFormData({
      email: userItem.email,
      name: userItem.name,
      role: userItem.role
    });
    setIsEditOpen(true);
  };

  const resetForm = () => {
    setFormData({
      email: "",
      name: "",
      role: "guest"
    });
    setSelectedUser(null);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case "superuser":
        return "role-superuser";
      case "administrator":
        return "role-administrator";
      default:
        return "role-guest";
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "superuser":
        return "Super User";
      case "administrator":
        return "Administrator";
      default:
        return "Guest";
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "superuser":
        return Shield;
      case "administrator":
        return UserCog;
      default:
        return UserIcon;
    }
  };

  const roleOptions = [
    { value: "guest", label: "Guest" },
    { value: "administrator", label: "Administrator" },
    { value: "superuser", label: "Super User" }
  ];

  if (user?.role !== "superuser") {
    return null;
  }

  return (
    <Layout user={user}>
      <div className="animate-fade-in">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="page-title">Manajemen User</h1>
            <p className="page-subtitle">Kelola pengguna sistem dan hak akses</p>
          </div>
          <Button 
            className="btn-accent"
            onClick={() => {
              resetForm();
              setIsCreateOpen(true);
            }}
            data-testid="add-user-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Tambah User
          </Button>
        </div>

        {/* Users Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner"></div>
          </div>
        ) : users.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {users.map((userItem) => {
              const RoleIcon = getRoleIcon(userItem.role);
              const isCurrentUser = userItem.user_id === user?.user_id;
              return (
                <Card 
                  key={userItem.user_id} 
                  className={`card-hover ${isCurrentUser ? 'ring-2 ring-blue-500' : ''}`}
                  data-testid={`user-card-${userItem.user_id}`}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4 overflow-hidden">
                      <Avatar className="h-12 w-12 flex-shrink-0">
                        <AvatarImage src={userItem.picture} alt={userItem.name} />
                        <AvatarFallback className="bg-slate-100 text-slate-600 text-lg">
                          {userItem.name?.charAt(0) || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <h3 className="font-semibold text-slate-900 truncate">
                          {userItem.name}
                          {isCurrentUser && (
                            <span className="ml-2 text-xs text-blue-600">(Anda)</span>
                          )}
                        </h3>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <Mail className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{userItem.email}</span>
                        </p>
                        {userItem.company_name && (
                          <p className="text-sm text-blue-600 flex items-center gap-1 mt-1">
                            <Building2 className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{userItem.company_name}</span>
                          </p>
                        )}
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border mt-2 ${getRoleBadgeClass(userItem.role)}`}>
                          <RoleIcon className="w-3 h-3" />
                          {getRoleLabel(userItem.role)}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => openEditDialog(userItem)}
                        data-testid={`edit-user-${userItem.user_id}`}
                      >
                        Edit Role
                      </Button>
                      {!isCurrentUser && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedUser(userItem);
                            setIsDeleteOpen(true);
                          }}
                          data-testid={`delete-user-${userItem.user_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="py-16">
              <div className="empty-state">
                <Users className="empty-state-icon" />
                <p className="empty-state-title">Belum ada pengguna</p>
                <p className="empty-state-description">
                  Tambahkan pengguna untuk memberikan akses ke sistem
                </p>
                <Button 
                  className="btn-accent"
                  onClick={() => {
                    resetForm();
                    setIsCreateOpen(true);
                  }}
                  data-testid="add-first-user-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Tambah User Pertama
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create Dialog */}
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Tambah User Baru</DialogTitle>
              <DialogDescription>
                Daftarkan pengguna baru untuk akses sistem via Google Login
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="form-label">Email Google *</Label>
                <Input
                  data-testid="input-user-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contoh@gmail.com"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Email ini akan digunakan untuk login dengan Google
                </p>
              </div>
              <div>
                <Label className="form-label">Nama *</Label>
                <Input
                  data-testid="input-user-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nama lengkap"
                />
              </div>
              <div>
                <Label className="form-label">Role *</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="select-user-role">
                    <SelectValue placeholder="Pilih role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsCreateOpen(false);
                resetForm();
              }}>
                Batal
              </Button>
              <Button className="btn-accent" onClick={handleCreate} data-testid="submit-user">
                Simpan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Perbarui informasi pengguna
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="form-label">Email</Label>
                <Input
                  value={formData.email}
                  disabled
                  className="bg-slate-50"
                />
              </div>
              <div>
                <Label className="form-label">Nama</Label>
                <Input
                  data-testid="edit-user-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nama lengkap"
                />
              </div>
              {selectedUser?.company_name && (
                <div>
                  <Label className="form-label">Perusahaan Asuransi</Label>
                  <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-md border border-blue-200">
                    <Building2 className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-700 font-medium">{selectedUser.company_name}</span>
                  </div>
                </div>
              )}
              <div>
                <Label className="form-label">Role</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="edit-user-role">
                    <SelectValue placeholder="Pilih role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsEditOpen(false);
                resetForm();
              }}>
                Batal
              </Button>
              <Button className="btn-accent" onClick={handleEdit} data-testid="submit-edit-user">
                Update
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Hapus Pengguna?</AlertDialogTitle>
              <AlertDialogDescription>
                Apakah Anda yakin ingin menghapus "{selectedUser?.name}"? 
                Pengguna ini akan kehilangan akses ke sistem.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Batal</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-user"
              >
                Hapus
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default UsersPage;
